import React, { ReactText, useState } from "react"
import ReactModal from "react-modal"
import { useMediaQuery } from "react-responsive"
import tw, { styled, css } from "twin.macro"
import { graphql, useStaticQuery } from "gatsby"
import Img from "gatsby-image"

import SlideSection from "../components/slideSection"
import NextPageButton from "../components/nextPageButton"
import IconDesign from "../icons/iconDesign"
import IconSkillset from "../icons/iconSkillset"
import IconLearning from "../icons/iconLearning"
import IconEnergy from '../icons/iconEnergy'
import { SlideProps } from "."

interface FeatureData {
  active: boolean
  icon: React.ReactNode
  title: ReactText
  name: ReactText
  details: ReactText
  iconLeft?: boolean,
  iconWidth?: string
  anchor: string
}

interface FeatureIconData {
  active: boolean,
  width?: string
}


const FeatureTop = styled.h3`${tw`font-sans uppercase text-xs md:text-lg tracking-widest`}`
const FeatureName = styled.h1`
  ${tw`font-text text-lg md:text-feature-slide-title font-bold tracking-tight whitespace-pre-line mb-3`}`
const FeatureDetail = styled.h2`
  ${tw`font-text text-sm tracking-wide whitespace-pre-line`}`

const FeatureIcon: React.FC<FeatureIconData> = (props) =>
  <div css={[tw`py-4 hover:opacity-75`,
  (!props.active && css`opacity: 0.2`),
  (props.width ? css`width: ${props.width}` : css`width: 1.75rem`)]}>
    {props.children}
  </div>

const FeatureContent: React.FC<FeatureData> = (props: FeatureData) =>
  <div css={[
    tw`flex flex-col cursor-pointer md:mt-4 items-center md:items-start`,
    !props.active && tw`hidden md:flex md:visible`,
    CONTAINER_DEBUG && tw`bg-blue-400`,
    props.iconLeft ? tw`md:flex-row` : tw`md:flex-row-reverse`
  ]}>
    <a href={`${props.anchor}`}>
    <FeatureIcon active={props.active} width={props.iconWidth}>
      {props.icon}
    </FeatureIcon>
    </a>
    <div css={[tw`flex-col flex-1 text-center`,
    css`max-width: 16rem`,
    // css`@media (min-width:768px) {
    //   max-width: 16rem
    // }`,
    CONTAINER_DEBUG && tw`bg-green-400`,
    props.iconLeft ? tw`md:(items-end ml-4 text-left)` : tw`md:(items-start mr-4 text-right)`]}>
      <div css={[tw`flex flex-col`, !props.active && tw`invisible`]}>
        <FeatureTop>{props.title}</FeatureTop>
        <FeatureName>{props.name}</FeatureName>
        <FeatureDetail>{props.details}</FeatureDetail>
      </div>
    </div>
  </div>

// Set this to true to give the blocks background colors
const CONTAINER_DEBUG = false;

const RelativeContainer = styled.div`
${tw`relative w-full h-full`}
${CONTAINER_DEBUG && tw`bg-red-400`}
`

// Wraps the 'content'
const ContentContainer = styled.div`
${tw`container mx-auto h-full w-full flex justify-center items-center`}
${CONTAINER_DEBUG && tw`bg-blue-400`}
`

// Constrains the content vertically so its centered on md+
const ContentVerticalConstraint = styled.div`
  max-height: 80%;
${tw`h-full flex flex-col md:flex-row justify-end md:justify-center z-10 pb-16`}
${CONTAINER_DEBUG && tw`bg-green-400`}
`

const FeatureColumn = styled.div`
${tw`flex flex-col`}
${CONTAINER_DEBUG && tw`bg-purple-400`}
`

interface FeatureSlideProps extends SlideProps {
  featureIndex: number
  anchor: string
}

const FeaturesSlide: React.FC<FeatureSlideProps> = ({
  slideVisible,
  featureIndex,
  anchor,
  nextButtonClicked,
}) => {
  const isSmall = useMediaQuery({ query: "(max-width: 767px)" })

  const data = useStaticQuery(query)

  let imageSource = data.featureImageLearning.childImageSharp.fluid
  switch (featureIndex) {
    case 1:
      imageSource = data.featureImageDesign.childImageSharp.fluid;
      break;
    case 3:
      imageSource = data.featureImageSkillset.childImageSharp.fluid
      break;
    case 2:
      imageSource = data.featureImageEnergy.childImageSharp.fluid
      break;
    default:
      break;
  }

  return (
    <SlideSection fullHeight>
      <RelativeContainer>
        <ContentContainer>
          <ContentVerticalConstraint>
            <FeatureColumn css={[tw`md:(mr-64 justify-start)`,
            // transitions
            tw`transition duration-500 ease-out delay-100`,
            !slideVisible && tw`opacity-0`
            ]}>
              <FeatureContent anchor={"#feature_learning"} active={featureIndex == 0} icon={<IconLearning />} iconWidth="2.5rem" title="Super-fast" name="Learning capacity" details="Each Apprentice comes with a willingness to learn and develop new skills" />
              <FeatureContent anchor={"#feature_design"} active={featureIndex == 1} icon={<IconDesign />} title="Ultra-modern" name="Design" details="Young people can introduce your team to new areas of interest and expertise" />
            </FeatureColumn>
            <FeatureColumn css={[tw`md:(ml-64 justify-end)`,
            // transitions
            tw`transition duration-500 ease-out delay-100`,
            !slideVisible && tw`opacity-0`
            ]}>
              <FeatureContent anchor={"#feature_skillsets"} active={featureIndex == 2} iconLeft icon={<IconEnergy />} iconWidth="2.4rem" title="Superior" name="Energy reserves" details="A MITO Automotive Apprentice will inject new passion and energy into your workshop" />
              {/* Spacer to make lift the top element */}
              <div css={[tw`md:h-16 sm:h-0`]}></div>
              <FeatureContent anchor={"#feature_energy"} active={featureIndex == 3} iconLeft icon={<IconSkillset />} iconWidth="1.84rem" title="Customisable" name="Skillsets" details="An Apprentice can be trained your way, according to the needs of your business" />
            </FeatureColumn>
          </ContentVerticalConstraint>
        </ContentContainer>

        <div css={[tw`absolute top-0 h-full w-full flex justify-center z-0`]}>
          <div css={[tw`flex h-full w-full`]}>
            <Img
              css={[tw`flex h-full w-full`]}
              fluid={imageSource}
              imgStyle={{ objectFit: 'contain' }}
            />
          </div>

          <div css={[tw`absolute bottom-0 mb-16`]}>
            <NextPageButton onClick={nextButtonClicked} />
          </div>
        </div>
      </RelativeContainer>

    </SlideSection>
  )
}
export const query = graphql`
  query {
    featureImageLearning: file(relativePath: { eq: "FeatureLearning.png" }) {
      childImageSharp {
        fluid(maxWidth: 1000, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    featureImageDesign: file(relativePath: { eq: "FeatureDesign.png" }) {
      childImageSharp {
        fluid(maxWidth: 1000, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    featureImageSkillset: file(relativePath: { eq: "FeatureSkillset.png" }) {
      childImageSharp {
        fluid(maxWidth: 1000, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    featureImageEnergy: file(relativePath: { eq: "FeatureEnergy.png" }) {
      childImageSharp {
        fluid(maxWidth: 1000, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
  }
`

export default FeaturesSlide
